import React from "react";
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  FunctionField,
  ChipField,
  useUpdate,
  useNotify,
  useRefresh,
  useRecordContext,
  Button,
  TopToolbar,
  EditButton,
} from "react-admin";
import {
  Chip,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Paper,
  Stack,
  Alert,
} from "@mui/material";

// Действия в заголовке
const ShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
);

// Компонент для отображения статуса с цветом
const StatusChip = () => {
  const record = useRecordContext();
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "sent":
        return "success";
      case "failed":
        return "error";
      case "retrying":
        return "warning";
      case "pending":
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case "sent":
        return "Отправлено";
      case "failed":
        return "Ошибка";
      case "retrying":
        return "Повтор";
      case "pending":
      default:
        return "Ожидает";
    }
  };

  if (!record) return null;

  return (
    <Chip
      label={getStatusLabel(record.status)}
      color={getStatusColor(record.status) as any}
      size="medium"
    />
  );
};

// Компонент для отображения типа уведомления
const TypeChip = () => {
  const record = useRecordContext();
  const getTypeLabel = (type: string) => {
    switch (type) {
      case "welcome":
        return "Добро пожаловать";
      case "password_change":
        return "Смена пароля";
      case "password_reset":
        return "Сброс пароля";
      case "follow":
        return "Подписка";
      case "invite":
        return "Приглашение";
      case "checkin_reminder":
        return "Напоминание о чекине";
      case "challenge_start":
        return "Начало челленджа";
      case "challenge_complete":
        return "Завершение челленджа";
      case "supplement_order_confirmed":
        return "Подтверждение заказа";
      case "payment_success":
        return "Успешная оплата";
      case "payment_failed":
        return "Ошибка оплаты";
      default:
        return type;
    }
  };

  if (!record) return null;

  return (
    <Chip label={getTypeLabel(record.type)} variant="outlined" size="medium" />
  );
};

// Компонент для отображения канала
const ChannelChip = () => {
  const record = useRecordContext();
  const getChannelColor = (channel: string) => {
    switch (channel) {
      case "email":
        return "primary";
      case "push":
        return "secondary";
      case "sms":
        return "info";
      default:
        return "default";
    }
  };

  if (!record) return null;

  return (
    <Chip
      label={record.channel.toUpperCase()}
      color={getChannelColor(record.channel) as any}
      size="medium"
    />
  );
};

// Кнопка для повторной отправки
const RetryButton = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const handleRetry = async () => {
    try {
      await update("notifications", {
        id: record.id,
        data: { action: "retry" },
      });
      notify("Уведомление добавлено в очередь для повторной отправки", {
        type: "success",
      });
      refresh();
    } catch (error) {
      notify("Ошибка при повторной отправке уведомления", { type: "error" });
    }
  };

  if (record.status?.toLowerCase() !== "failed") {
    return null;
  }

  return (
    <Button
      label="Повторить отправку"
      onClick={handleRetry}
      disabled={isLoading}
      variant="contained"
      color="primary"
    />
  );
};

export const NotificationShow = () => {
  return (
    <Show title="Детали уведомления" actions={<ShowActions />}>
      <SimpleShowLayout>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Заголовок с чипами */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <StatusChip />
              <TypeChip />
              <ChannelChip />
            </Stack>
            <Typography variant="h4" component="h1" gutterBottom>
              <TextField source="title" label="" />
            </Typography>
          </Paper>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Основная информация */}
            <Box>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Основная информация
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        ID уведомления
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <TextField source="id" label="" />
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Получатель
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <TextField source="recipient" label="" />
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Пользователь
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <FunctionField
                          label=""
                          render={(record: any) =>
                            record.user
                              ? `${record.user.username} (${record.user.email})`
                              : "Система"
                          }
                        />
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Шаблон
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <TextField source="templateName" label="" />
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Количество попыток
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <TextField source="retryCount" label="" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            {/* Временные метки */}
            <Box>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Временные метки
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Создано
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <DateField source="createdAt" label="" showTime />
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Обновлено
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <DateField source="updatedAt" label="" showTime />
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Отправлено
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <DateField source="sentAt" label="" showTime />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            {/* Содержание */}
            <Box>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Содержание уведомления
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200"
                    }}
                  >
                    <Typography variant="body1" component="div">
                      <TextField source="content" label="" />
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            </Box>

            {/* Ошибка (если есть) */}
            <Box>
              <FunctionField
                label=""
                render={(record: any) =>
                  record.errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Ошибка отправки
                      </Typography>
                      <Typography variant="body2">
                        <TextField source="errorMessage" label="" />
                      </Typography>
                    </Alert>
                  )
                }
              />
            </Box>

            {/* Дополнительные данные */}
            <Box>
              <FunctionField
                label=""
                render={(record: any) =>
                  record.data && (
                    <Card elevation={2}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          Дополнительные данные
                        </Typography>
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "grey.200",
                            overflow: "auto"
                          }}
                        >
                          <pre
                            style={{
                              margin: 0,
                              fontSize: "13px",
                              lineHeight: 1.5,
                              fontFamily: "Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
                            }}
                          >
                            {JSON.stringify(record.data, null, 2)}
                          </pre>
                        </Paper>
                      </CardContent>
                    </Card>
                  )
                }
              />
            </Box>

            {/* Действия */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <RetryButton />
              </Box>
            </Box>
          </Box>
        </Box>
      </SimpleShowLayout>
    </Show>
  );
};
